import { SaveContactApiResponseSuccess, SaveContactParams } from '@de/types';
import Lead from '@de/types/leadDelivery';
import type { FlowState } from '@de/providers/dynamicExperienceReducer';
import buildLeadObject from '@de/utils/buildLeadObject';
import { saveContact } from '@de/utils/data-fetch/contactAPI';
import { transformFlowStateToLeadInputs } from '@de/utils/dataFromFlowUtils';

export interface HandleContactSubmissionParams {
  flowState: Record<string, unknown>;
  correlationId: string;
  publisher?: string;
}

export interface HandleContactSubmissionResult {
  success: boolean;
  error?: string;
  data?: SaveContactApiResponseSuccess;
}

const REQUIRED_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'phoneNumber',
  // some endpoints uses the zipCode instead of zip, so this files follows the same pattern as the
  // supersonic-ui project
  'zip',
] as const;

export const handleContactSubmission = async (
  params: HandleContactSubmissionParams,
): Promise<HandleContactSubmissionResult> => {
  const { flowState, correlationId, publisher = 'Unknown' } = params;
  try {
    const contactData: SaveContactParams | null = buildContactData(
      flowState as FlowState,
      correlationId,
      publisher,
    );

    if (!contactData) {
      return {
        success: false,
        error: 'Missing required contact information',
      };
    }

    const result = await saveContact(contactData);

    return result.success
      ? { success: true, data: result.data }
      : {
          success: false,
          error: result.error?.error || 'Failed to save contact',
        };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
};

const buildContactData = (
  flowState: FlowState,
  correlationId: string,
  publisher: string,
): SaveContactParams | null => {
  const inputs = transformFlowStateToLeadInputs(flowState);

  const missingFields = REQUIRED_FIELDS.filter((field) => !inputs[field]);

  if (missingFields.length > 0) {
    return null;
  }

  const lead: Lead = buildLeadObject(inputs);

  return {
    degree: getValueByLeadField('degree', flowState),
    subject: getValueByLeadField('subject', flowState),
    category: getValueByLeadField('category', flowState),
    cohesionSessionId:
      (typeof window !== 'undefined' && window?._Cohesion?.sessionId) ||
      correlationId,
    lead,
    publisher,
  };
};

const getValueByLeadField = (
  fieldName: string,
  flowState: FlowState,
): string => {
  const stepData = Object.values(flowState).find(
    (data) => data?.leadFieldAssociated === fieldName,
  );

  if (stepData?.option) {
    const option = Array.isArray(stepData.option)
      ? stepData.option[0]
      : stepData.option;
    return option?.value?.toString() || option?.text?.toString() || 'Unknown';
  }

  return 'Unknown';
};
